import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDuration } from "../lib/api";

export default function CertificatePage({ publicView = false }) {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const url = publicView ? `/certificates/${id}` : `/courses/${id}/certificate`;
    api.get(url).then((r) => setCert(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id, publicView]);

  const copyShareLink = () => {
    const link = `${window.location.origin}/cert/${cert.id}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Share link copied — anyone can view your certificate with it!"));
  };

  if (err) return (
    <div className="p-10 pt-28 text-center" data-testid="certificate-locked">
      <p className="font-display text-lg" style={{ color: "var(--gold)" }}>🏆 {publicView ? "CERTIFICATE NOT FOUND" : "CERTIFICATE LOCKED"}</p>
      <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>{err}</p>
      {publicView
        ? <Link to="/courses" className="btn-primary inline-block mt-5">BROWSE COURSES</Link>
        : <Link to={`/learn/${id}`} className="btn-primary inline-block mt-5">CONTINUE LEARNING</Link>}
    </div>
  );
  if (!cert) return <p className="p-10 pt-28 text-[var(--forge-dim)]">Loading…</p>;

  const letters = cert.user_name.split("");
  return (
    <div className="cert-page" data-testid="certificate-page">
      <div className="intro-grid" />
      {[...Array(16)].map((_, i) => (
        <span key={i} className="intro-spark"
          style={{ left: `${(i * 137 + 31) % 100}%`, animationDuration: `${2.4 + (i % 5) * 0.6}s`,
            animationDelay: `${(i % 7) * 0.4}s`, background: i % 3 ? "#38b6ff" : "#f5c04e" }} />
      ))}
      <div className="cert-frame" data-testid="certificate-frame">
        <span className="cert-corner tl" /><span className="cert-corner tr" />
        <span className="cert-corner bl" /><span className="cert-corner br" />
        <div className="logo" style={{ justifyContent: "center", cursor: "default" }}>
          <div className="cube"></div>UNREAL<em>ACADEMY</em>
        </div>
        <p className="cert-title">CERTIFICATE OF COMPLETION</p>
        <p className="cert-sub">THIS CERTIFIES THAT</p>
        <h1 className="cert-name" data-testid="certificate-name">
          {letters.map((c, i) => (
            <span key={i} className="intro-letter" style={{ animationDelay: `${0.3 + i * 0.05}s` }}>
              {c === " " ? "\u00A0" : c}
            </span>
          ))}
        </h1>
        <p className="cert-sub">HAS SUCCESSFULLY COMPLETED</p>
        <h2 className="cert-course" data-testid="certificate-course">{cert.course_title}</h2>
        <div className="cert-meta">
          <span>{cert.level.toUpperCase()}</span><span>·</span>
          <span>{cert.lecture_count} LECTURES</span><span>·</span>
          <span>{fmtDuration(cert.total_duration_min).toUpperCase()}</span>
        </div>
        <div className="cert-line" />
        <div className="cert-foot">
          <div className="cert-sign">
            <p className="sig">{cert.instructor}</p>
            <p className="lbl">INSTRUCTOR</p>
          </div>
          <div className="cert-sign" style={{ textAlign: "right" }}>
            <p className="sig">{new Date(cert.issued_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="lbl">CERT ID · <span data-testid="certificate-id">{cert.id}</span></p>
          </div>
        </div>
      </div>
      <div className="cert-actions">
        <button onClick={() => window.print()} className="btn-primary" data-testid="cert-print-btn">⬇ DOWNLOAD / PRINT</button>
        {publicView ? (
          <Link to={`/course/${cert.course_id}`} className="btn-ghost" data-testid="cert-view-course-btn">VIEW THIS COURSE →</Link>
        ) : (
          <>
            <button onClick={copyShareLink} className="btn-ghost" data-testid="cert-share-btn">🔗 COPY SHARE LINK</button>
            <Link to="/my-learning" className="btn-ghost" data-testid="cert-back-btn">← MY LEARNING</Link>
          </>
        )}
      </div>
      {publicView && (
        <p className="text-center text-sm mt-4" style={{ color: "var(--muted)" }} data-testid="cert-public-cta">
          Earned on <Link to="/" style={{ color: "var(--bp)" }}>Unreal Academy</Link> — project-based Unreal Engine courses.
        </p>
      )}
    </div>
  );
}
